(() => {
  const boot = (root) => {
    if (root.dataset.bp2Ready) return;
    root.dataset.bp2Ready = 'true';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const current = new URL(location.href);
    const transient = new Set(['view','partner','contact_posted','form_type','return_to']);
    root.querySelectorAll('[data-bp2-route-link]').forEach((a) => {
      const dest = new URL(a.href, location.origin);
      if (dest.origin !== location.origin) return;
      for (const [key,value] of current.searchParams) {
        if (!transient.has(key) && !dest.searchParams.has(key)) {
          current.searchParams.getAll(key).forEach((item) => dest.searchParams.append(key,item));
        }
      }
      a.href = dest.pathname + dest.search + dest.hash;
      if (dest.pathname === current.pathname && dest.searchParams.get('view') === current.searchParams.get('view')) a.setAttribute('aria-current','page');
    });
    const menu = root.querySelector('.bp2-nav-menu');
    document.addEventListener('click', (event) => {
      if (menu?.open && !menu.contains(event.target)) menu.open = false;
    });
    menu?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { menu.open = false; menu.querySelector('summary').focus(); }
    });
    const header = document.querySelector('.ds-header');
    const measure = () => {
      let node = header;
      let height = 0;
      while (node && node !== document.body) {
        if (['sticky','fixed'].includes(getComputedStyle(node).position)) height = Math.max(height,node.getBoundingClientRect().height);
        node = node.parentElement;
      }
      root.style.setProperty('--bp2-header-height', height + 'px');
    };
    measure();
    if (header && 'ResizeObserver' in window) new ResizeObserver(measure).observe(header);
    const sticky = root.querySelector('[data-bp2-mobile-apply]');
    const application = root.querySelector('#bp2-apply');
    if (sticky && application && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => { sticky.hidden = entries[0].isIntersecting; },{threshold:0}).observe(application);
    }
    const smallScreen = window.matchMedia('(max-width:700px)');
    const prep = root.querySelector('[data-bp2-prep]');
    if (prep) {
      prep.open = !smallScreen.matches;
      smallScreen.addEventListener('change',() => { prep.open = !smallScreen.matches; });
    }
    const form = root.querySelector('#bp-partner-form');
    const result = root.querySelector('[data-bp2-result]');
    const steps = [...root.querySelectorAll('[data-bp2-form-step]')];
    if (!form || !steps.length) {
      if (result) { result.focus({preventScroll:true}); result.scrollIntoView({block:'center'}); }
      return;
    }
    const next = form.querySelector('[data-bp2-next]');
    const back = form.querySelector('[data-bp2-back]');
    const submit = form.querySelector('[data-bp2-submit]');
    const final = form.querySelector('[data-bp2-final]');
    const errorSummary = form.querySelector('[data-bp2-error-summary]');
    const originalSend = submit.textContent.trim();
    let active = 0;
    let pending = false;
    const resetSend = () => { pending = false; submit.disabled = false; submit.removeAttribute('aria-busy'); submit.firstChild.textContent = originalSend.replace(/[↗→]$/,'').trim(); };
    const syncReturn = () => {
      const target = form.querySelector('[name=return_to]');
      if (!target) return;
      const url = new URL(location.href);
      url.searchParams.delete('contact_posted');
      url.hash = 'bp-partner-form';
      target.value = url.pathname + url.search + url.hash;
    };
    syncReturn();
    const campaign = form.querySelector('[data-bp2-campaign]');
    if (campaign) campaign.value = ['utm_source','utm_medium','utm_campaign','utm_content','ref','affiliate'].flatMap((key) => current.searchParams.getAll(key).map((v) => key + '=' + v.slice(0,100))).join('; ');
    const showStep = (index, focus = true) => {
      active = Math.max(0,Math.min(2,index));
      steps.forEach((el,i) => { el.hidden = i !== active; });
      form.querySelectorAll('[data-bp2-progress-step]').forEach((el,i) => {
        if (i === active) el.setAttribute('aria-current','step'); else el.removeAttribute('aria-current');
        el.dataset.done = String(i < active);
      });
      form.querySelector('[data-bp2-progress-fill]').style.width = ((active + 1) / 3 * 100) + '%';
      back.hidden = active === 0;
      next.hidden = active === 2;
      next.firstChild.textContent = form.querySelector(active === 0 ? '[data-bp2-continue]' : '[data-bp2-review-button]').textContent;
      submit.hidden = active !== 2;
      final.hidden = active !== 2;
      errorSummary.hidden = true;
      if (focus) {
        const title = steps[active].querySelector('legend,h3');
        title.tabIndex = -1;
        title.focus({preventScroll:true});
        form.scrollIntoView({behavior:reduce.matches?'auto':'smooth',block:'start'});
      }
    };
    const validate = (container) => {
      let first;
      container.querySelectorAll('input,select,textarea').forEach((el) => {
        const invalid = !el.disabled && !el.checkValidity();
        const error = form.querySelector('[data-bp2-error-for="'+el.id+'"]');
        if (invalid) {
          el.setAttribute('aria-invalid','true');
          if (error) el.setAttribute('aria-errormessage',error.id);
          if (error) { error.textContent = el.validationMessage; error.hidden = false; }
          if (!first) first = el;
        } else {
          el.removeAttribute('aria-invalid');
          if (error) error.hidden = true;
        }
      });
      errorSummary.hidden = !first;
      if (first) { first.focus(); first.reportValidity(); return false; }
      return true;
    };
    const buildReview = () => {
      const target = form.querySelector('[data-bp2-review]');
      target.replaceChildren();
      steps.slice(0,2).forEach((source,index) => {
        const group = document.createElement('section'); group.className = 'bp2-review-group';
        const head = document.createElement('div'); head.className = 'bp2-review-heading';
        const heading = document.createElement('h4'); heading.textContent = source.querySelector('legend').textContent;
        const edit = document.createElement('button'); edit.type = 'button'; edit.textContent = form.querySelector('[data-bp2-edit-label]').textContent;
        edit.setAttribute('aria-label',edit.textContent + ': ' + heading.textContent);
        edit.addEventListener('click',() => showStep(index));
        head.append(heading,edit);
        const list = document.createElement('dl');
        source.querySelectorAll('[data-bp2-label]').forEach((input) => {
          const row = document.createElement('div');
          if (input.tagName === 'TEXTAREA') row.className = 'bp2-review-wide';
          const term = document.createElement('dt'); term.textContent = input.dataset.bp2Label;
          const definition = document.createElement('dd');
          definition.textContent = input.tagName === 'SELECT' ? (input.value ? input.selectedOptions[0].textContent : root.dataset.bp2Empty) : input.value.trim() || root.dataset.bp2Empty;
          row.append(term,definition); list.append(row);
        });
        group.append(head,list); target.append(group);
      });
    };
    form.querySelectorAll('input,select,textarea').forEach((input) => {
      input.addEventListener('input',() => {
        if (input.checkValidity()) {
          input.removeAttribute('aria-invalid');
          const error = form.querySelector('[data-bp2-error-for="'+input.id+'"]');
          if (error) error.hidden = true;
        }
      });
    });
    root.querySelectorAll('[data-bp2-sector]').forEach((a) => a.addEventListener('click',() => {
      const select = form.querySelector('#bp2-business_type');
      if (select && [...select.options].some((o) => o.value === a.dataset.bp2Sector)) select.value = a.dataset.bp2Sector;
      if (active === 2) { buildReview(); }
    }));
    form.addEventListener('bp2:prefill',() => { if (active === 2) buildReview(); });
    const urlSector = current.searchParams.get('sector');
    const sectorInput = form.querySelector('#bp2-business_type');
    if (urlSector && sectorInput && [...sectorInput.options].some((o) => o.value === urlSector)) sectorInput.value = urlSector;
    next.addEventListener('click',() => {
      if (!validate(steps[active])) return;
      if (active === 1) buildReview();
      showStep(active + 1);
    });
    back.addEventListener('click',() => showStep(active - 1));
    form.addEventListener('submit',(event) => {
      if (pending) { event.preventDefault(); return; }
      if (active < 2) {
        event.preventDefault();
        if (validate(steps[active])) { if (active === 1) buildReview(); showStep(active + 1); }
        return;
      }
      for (let i = 0;i < 2;i++) {
        if ([...steps[i].querySelectorAll('input,select,textarea')].some((el) => !el.disabled && !el.checkValidity())) {
          event.preventDefault(); showStep(i); validate(steps[i]); return;
        }
      }
      if (!validate(final)) { event.preventDefault(); return; }
      syncReturn();
      pending = true; submit.disabled = true; submit.setAttribute('aria-busy','true');
      submit.firstChild.textContent = root.dataset.bp2Sending;
      queueMicrotask(() => { if (event.defaultPrevented) resetSend(); });
      window.setTimeout(resetSend,12000);
    });
    form.noValidate = true;
    form.querySelector('[data-bp2-progress]').hidden = false;
    root.classList.add('is-enhanced');
    showStep(0,false);
    window.addEventListener('pageshow',resetSend);
    if (result) { result.focus({preventScroll:true}); result.scrollIntoView({block:'center'}); }
  };
  const init = (scope) => scope.querySelectorAll('[data-business-v2]').forEach(boot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => init(document)); else init(document);
  document.addEventListener('shopify:section:load',(event) => init(event.target));
})();
